import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { auditLogAdapter } from '../shared-audit.adapters';
import * as actions from './trip-manager.actions';
import {
  PostedAircraftTripSkeleton,
  PostedAircraftTripAudit,
  PostedAircraftTripLegAudit
} from '../../+models/trip-manager-audit.model';

// States
interface PostedAircraftTripSkeletonsState extends EntityState<PostedAircraftTripSkeleton> {
  loading: boolean;
  loaded: boolean;
  error: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface PostedAircraftTripAuditState extends EntityState<PostedAircraftTripAudit> {
  loading: boolean;
  loaded: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
  error: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface PostedAircraftTripLegAuditState extends EntityState<PostedAircraftTripLegAudit> {
  loading: boolean;
  loaded: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
  error: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

// Adapters
const postedAircraftTripSkeletonsAdapter = createEntityAdapter<PostedAircraftTripSkeleton>({
  selectId: p => p.postedAircraftTripId
});

// Entity selectors
export const postedAircraftTripSkeletonsSelectors =
  postedAircraftTripSkeletonsAdapter.getSelectors();
export const postedAircraftTripAuditSelectors = auditLogAdapter.getSelectors();
export const postedAircraftTripLegAuditSelectors = auditLogAdapter.getSelectors();

// State
const initialPostedAircraftTripSkeletonsState = postedAircraftTripSkeletonsAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: null
});

const initialPostedAircraftTripAuditState = auditLogAdapter.getInitialState({
  loading: false,
  loaded: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null,
  error: null
});

const initialPostedAircraftTripLegAuditState = auditLogAdapter.getInitialState({
  loading: false,
  loaded: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null,
  error: null
});

export interface State {
  postedAircraftTripSkeletons: PostedAircraftTripSkeletonsState;
  postedAircraftTripAudit: PostedAircraftTripAuditState;
  postedAircraftTripLegAudit: PostedAircraftTripLegAuditState;
}

const initialState: State = {
  postedAircraftTripSkeletons: initialPostedAircraftTripSkeletonsState,
  postedAircraftTripAudit: initialPostedAircraftTripAuditState,
  postedAircraftTripLegAudit: initialPostedAircraftTripLegAuditState
};

// Reducer
const tripManagerReducer = createReducer<State>(
  initialState,
  on(actions.ResetTripManager, () => initialState),

  on(actions.LoadPostedAircraftTripSkeletons, state => ({
    ...state,
    postedAircraftTripSkeletons: {
      ...state.postedAircraftTripSkeletons,
      loading: true,
      loaded: false,
      error: null
    }
  })),

  on(actions.PostedAircraftTripSkeletonsLoaded, (state, action) => ({
    ...state,
    postedAircraftTripSkeletons: {
      ...state.postedAircraftTripSkeletons,
      ...postedAircraftTripSkeletonsAdapter.setAll(
        action.payload,
        state.postedAircraftTripSkeletons
      ),
      loading: false,
      loaded: true,
      error: null
    }
  })),

  on(actions.LoadPostedAircraftTripAudit, (state, action) => ({
    ...state,
    postedAircraftTripAudit: {
      ...state.postedAircraftTripAudit,
      lastAuditLogId:
        state.postedAircraftTripAudit.selectedId === action.postedAircraftTripId
          ? state.postedAircraftTripAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false,
      error: null
    }
  })),

  on(actions.PostedAircraftTripAuditLoaded, (state, action) => ({
    ...state,
    postedAircraftTripAudit: {
      ...state.postedAircraftTripAudit,
      ...(!state.postedAircraftTripAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.postedAircraftTripAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.postedAircraftTripAudit)),
      loading: false,
      loaded: true,
      error: null,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId:
        action.audits.resultSet && action.audits.resultSet.length > 0
          ? Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
          : state.postedAircraftTripAudit.lastAuditLogId
    }
  })),

  on(actions.LoadPostedAircraftTripLegAudit, (state, action) => ({
    ...state,
    postedAircraftTripLegAudit: {
      ...state.postedAircraftTripLegAudit,
      lastAuditLogId:
        state.postedAircraftTripLegAudit.selectedId === action.postedFlightId
          ? state.postedAircraftTripLegAudit.lastAuditLogId
          : null,
      loading: true,
      loaded: false,
      error: null
    }
  })),

  on(actions.PostedAircraftTripLegAuditLoaded, (state, action) => ({
    ...state,
    postedAircraftTripLegAudit: {
      ...state.postedAircraftTripLegAudit,
      ...(!state.postedAircraftTripLegAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.postedAircraftTripLegAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.postedAircraftTripLegAudit)),
      loading: false,
      loaded: true,
      error: null,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId:
        action.audits.resultSet && action.audits.resultSet.length > 0
          ? Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
          : state.postedAircraftTripLegAudit.lastAuditLogId
    }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return tripManagerReducer(state, action);
}
